import React, { useState } from "react";
import { Lock, Loader2, Eye } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { grantAdminUnlock, grantDemoUnlock } from "@/lib/prelaunch";
import { LogoMark } from "@/components/Logo";
import { toast } from "sonner";

/**
 * PreviewAccess — buyer-preview sign-in page.
 *
 * Melissa shares this URL + the demo credentials with potential buyers so
 * they can walk through the full app during pre-launch. The page is
 * whitelisted in `PRELAUNCH_ALLOWED_PATHS` so it renders even when
 * REACT_APP_PRELAUNCH=1.
 *
 * Only accounts whose /auth/me response carries `is_demo=true` (or
 * `is_admin=true`, in case Melissa opens this URL herself) are accepted —
 * every other login attempt is rejected and the session cleared.
 */
export default function PreviewAccess() {
  const { login, logout } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErr("");
    setSubmitting(true);
    try {
      const u = await login(identifier.trim(), password);
      if (!u || !(u.is_demo || u.is_admin)) {
        logout();
        setErr("This URL only accepts the MystiCast demo preview account.");
        setSubmitting(false);
        return;
      }
      if (u.is_demo) {
        grantDemoUnlock();
        toast.success("Demo preview unlocked. Welcome to MystiCast.");
      } else {
        grantAdminUnlock();
        toast.success(`Welcome back, ${u.username}.`);
      }
      // Hard-reload so the prelaunch gate re-evaluates and the full app
      // (not the Coming Soon splash) renders on this browser.
      window.location.assign("/");
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Invalid credentials";
      setErr(typeof msg === "string" ? msg : "Login failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden flex items-center justify-center p-6 bg-[#0A0A0C]"
      data-testid="preview-access-root"
    >
      {/* Ambient neon orbs + grid — reuse the Coming Soon aesthetic. */}
      <div className="neon-orb" style={{ background: "#FF1053", width: 320, height: 320, top: -80, left: -80 }} />
      <div className="neon-orb" style={{ background: "#CCFF00", width: 260, height: 260, bottom: -60, right: -60, animationDelay: "-3s" }} />
      <div className="cyber-grid" />
      <div className="crt-scanlines" />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <LogoMark size={44} />
          <div className="sc-heading text-2xl font-black tracking-tight">
            <span style={{ color: "var(--sc-pink)" }} className="text-glow-pink">MYSTI</span>
            <span style={{ color: "var(--sc-volt)" }} className="text-glow-volt">CAST</span>
          </div>
        </div>

        <div className="term-card cyan p-6 sm:p-8" style={{ background: "rgba(10,10,12,0.98)" }}>
          <div className="sc-label mb-3 flex items-center gap-2" style={{ color: "var(--sc-cyan)" }}>
            <Eye className="w-3 h-3" /> BUYER PREVIEW · VIEW-ONLY ACCESS
          </div>
          <h1 className="sc-heading text-2xl sm:text-3xl font-black mb-2" data-testid="preview-access-title">
            Sign in to preview MystiCast
          </h1>
          <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
            This is a read-only walkthrough of the platform. Posting, chat,
            tipping, subscriptions, and streaming are disabled on this
            account. Sessions expire automatically after two hours.
          </p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label
                className="sc-label text-[10px] block mb-1.5"
                style={{ color: "var(--sc-cyan)" }}
                htmlFor="preview-identifier"
              >
                USERNAME OR EMAIL
              </label>
              <input
                id="preview-identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoFocus
                autoComplete="username"
                required
                data-testid="preview-identifier"
                className="w-full px-4 py-2.5 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none focus:ring-1 focus:ring-[color:var(--sc-cyan)]/40 rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
                placeholder="demo"
              />
            </div>
            <div>
              <label
                className="sc-label text-[10px] block mb-1.5"
                style={{ color: "var(--sc-cyan)" }}
                htmlFor="preview-password"
              >
                PASSWORD
              </label>
              <input
                id="preview-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="preview-password"
                className="w-full px-4 py-2.5 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none focus:ring-1 focus:ring-[color:var(--sc-cyan)]/40 rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div
                className="text-xs px-3 py-2 rounded border border-[color:var(--sc-pink)]/40 bg-[color:var(--sc-pink)]/5 text-[color:var(--sc-pink)]"
                data-testid="preview-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="preview-submit"
              className="w-full sc-btn text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" /> Enter preview
                </>
              )}
            </button>
          </form>

          <div className="sc-mono text-[10px] text-zinc-600 mt-5 leading-relaxed">
            Credentials are issued by MystiCast on request. Login attempts
            are rate-limited.
          </div>
        </div>
      </div>
    </div>
  );
}
